
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GameType, Card } from '../../types';
import {
  searchExternalCards,
  saveDeck,
  listUserDecks,
  getUserDeck,
  deleteUserDeck,
  saveActionLog,
  getCardgames,
  getGameIcon,
  getGameFormats
} from '../../services/supabaseService';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { CardImage } from '../../components/CardImage';
import { CardDetailModal } from '../../components/CardDetailModal';
import { ImportDeckModal } from '../../components/game/ImportDeckModal';
import {
  DeckBuilderSettingsModal,
  DeckBuilderSettings,
  loadDeckBuilderSettings,
  saveDeckBuilderSettings
} from '../../components/game/DeckBuilderSettingsModal';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { getRulesByGame, getSlugByGame, sorceryMax, getGameNameById } from '../../constants/deckRules';
import { generateOnePieceDeckText, generateGenericDeckText } from '../../utils/deckExporter';
import { GAME_FILTERS_CONFIG, pretty } from './Search';
import { GameSelector } from '../../components/GameSelector';

interface DeckBuilderPageProps {
  activeGame: number | 'All';
  setActiveGame?: (game: number | 'All') => void;
  dbGames?: any[];
}

interface DeckItem {
  card: Card;
  quantity: number;
  section: string;
}

const getCardCostValue = (card: any): number => {
  if (!card) return 999;
  const val = card.cost ?? card.playCost ?? card.play_cost ?? card.energyCost ?? card.energy_cost ?? card.cmc ?? card.manaCost ?? card.level ?? card.pitch;
  if (val === undefined || val === null || val === '') return 999;
  const parsed = Number(val);
  return isNaN(parsed) ? 999 : parsed;
};

// Godzilla Game Specific Rule Helpers
export const getGodzillaBaseCode = (c: any): string => {
  if (!c) return '';
  const raw = String(c.code || c.external_id || c.number || c.id || '').trim();
  // Strip trailing '+' so ESD01-001 and ESD01-001+ have the same base number
  return raw.replace(/\+$/, '').trim().toUpperCase();
};

export const getGodzillaMonsterRank = (c: any): number | null => {
  if (!c) return null;
  const g = String(c.grade || c.rank || '').trim();
  if (g === 'Ⅰ' || g === 'I' || g === '1' || g === '①') return 1;
  if (g === 'Ⅱ' || g === 'II' || g === '2' || g === '②') return 2;
  if (g === 'Ⅲ' || g === 'III' || g === '3' || g === '③') return 3;
  if (g === 'Ⅳ' || g === 'IV' || g === '4' || g === '④') return 4;
  return null;
};

export const isGodzillaInvade2 = (c: any): boolean => {
  if (!c) return false;
  return Number(c.step_icon) === 2 || Number(c.invade) === 2 || String(c.step_icon) === '2';
};

export const getGodzillaColor = (c: any): 'red' | 'blue' | 'white' | 'other' => {
  if (!c) return 'other';
  const col = String(c.color || '').toLowerCase().trim();
  if (col.includes('red') || col.includes('vermelh')) return 'red';
  if (col.includes('blue') || col.includes('azul')) return 'blue';
  if (col.includes('white') || col.includes('branc')) return 'white';
  return 'other';
};

const DetailField: React.FC<{
  label: string;
  value: string | number;
  highlight?: string;
  isCircle?: boolean;
  icon?: string;
}> = ({ label, value, highlight = 'text-white', isCircle, icon }) => (
  <div className="space-y-0.5">
    <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">{label}</p>
    <div className="bg-slate-950/40 border border-white/5 rounded-lg px-2.5 py-1.5 flex items-center justify-between group hover:border-purple-500/30 transition-colors">
      <span className={`text-[11px] font-bold ${highlight} truncate`}>{value}</span>
      {isCircle ? (
        <span className="w-5 h-5 bg-emerald-500 text-slate-950 text-[9px] font-black rounded-full flex items-center justify-center shrink-0 ml-2">
          {value}
        </span>
      ) : icon ? (
        <i className={`${icon} text-slate-500 text-[10px] ml-2`}></i>
      ) : null}
    </div>
  </div>
);

interface DeckFormatOption {
  id?: number | string | null;
  name: string;
  slug: string;
  description?: string | null;
}

interface CatalogCardItemProps {
  card: Card;
  inDeckCount: number;
  clickAction?: 'add' | 'preview';
  showDeckQuantityBadge?: boolean;
  showCardNames?: boolean;
  settings?: DeckBuilderSettings;
  idx?: number;
  onPreview: (card: Card) => void;
  onAdd: (card: Card) => void;
}

const CatalogCardItem = React.memo<CatalogCardItemProps>(({
  card,
  inDeckCount,
  clickAction: explicitClickAction,
  showDeckQuantityBadge: explicitShowBadge,
  showCardNames: explicitShowNames,
  settings,
  onPreview,
  onAdd
}) => {
  const clickAction = explicitClickAction ?? settings?.clickAction ?? 'add';
  const showDeckQuantityBadge = explicitShowBadge ?? settings?.showDeckQuantityBadge ?? true;
  const showCardNames = explicitShowNames ?? settings?.showCardNames ?? true;

  return (
    <div
      onClick={() => {
        if (clickAction === 'preview') {
          onPreview(card);
        } else {
          onAdd(card);
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onPreview(card);
      }}
      className="group relative cursor-pointer active:scale-[0.98]"
      title={clickAction === 'preview' ? "Clique para ver detalhes" : "Clique para adicionar ao baralho (botão direito p/ detalhes)"}
    >
      <div className={`aspect-[3/4.2] rounded-xl overflow-hidden bg-slate-800 border transition-colors duration-150 shadow-md group-hover:shadow-purple-500/10 ${inDeckCount > 0 ? 'border-purple-500 shadow-purple-500/20' : 'border-slate-800 hover:border-purple-500/40'}`}>
        <CardImage
          src={card.imageUrl}
          alt={card.name}
          game={card.game}
          gameId={card.game_id}
          cardBackUrl={card.cardBackUrl || card.card_back_url}
          className="w-full h-full object-cover"
        />

        {showDeckQuantityBadge && inDeckCount > 0 && (
          <div className="absolute top-2 right-2 bg-purple-600 text-white w-5 h-5 rounded-lg flex items-center justify-center text-[9px] font-black shadow-xl border border-purple-400/30">
            {inDeckCount}
          </div>
        )}
      </div>
      {showCardNames && (
        <div className="mt-1.5 text-center px-0.5">
          <p className="text-[9px] font-black uppercase text-slate-400 tracking-tight truncate group-hover:text-white transition-colors">
            {card.name}
          </p>
        </div>
      )}
    </div>
  );
});

interface DeckGridItemProps {
  item: DeckItem;
  showCostPills?: boolean;
  clickAction?: 'add' | 'preview';
  settings?: DeckBuilderSettings;
  onAdd: (card: Card) => void;
  onRemove: (cardId: string | number, section: string) => void;
  onPreview: (card: Card) => void;
}

const DeckGridItem = React.memo<DeckGridItemProps>(({
  item,
  showCostPills: explicitShowCostPills,
  clickAction: explicitClickAction,
  settings,
  onAdd,
  onRemove,
  onPreview
}) => {
  const showCostPills = explicitShowCostPills ?? settings?.showCostPills ?? true;
  const clickAction = explicitClickAction ?? settings?.clickAction ?? 'add';
  const costVal = getCardCostValue(item.card);

  return (
    <div
      onContextMenu={(e) => {
        e.preventDefault();
        onPreview(item.card);
      }}
      className="group relative bg-slate-950/80 rounded-xl border border-slate-800 hover:border-purple-500/60 overflow-hidden shadow-lg transition-colors flex flex-col"
    >
      <div
        className="relative aspect-[3/4.2] w-full bg-slate-950 overflow-hidden cursor-pointer"
        onClick={() => {
          onPreview(item.card);
        }}
        title="Clique para ver detalhes"
      >
        <CardImage
          src={item.card.imageUrl}
          alt={item.card.name}
          game={item.card.game}
          gameId={item.card.game_id}
          cardBackUrl={item.card.cardBackUrl || item.card.card_back_url}
          className="w-full h-full object-cover"
        />

        {showCostPills && costVal !== 999 && (
          <div className="absolute top-1 left-1 bg-slate-950/95 text-amber-300 border border-amber-500/50 text-[8px] font-black h-4 px-1 rounded shadow-md z-10 flex items-center space-x-0.5">
            <i className="fas fa-bolt text-[6px]"></i>
            <span>{costVal}</span>
          </div>
        )}

        <div className="absolute top-1 right-1 bg-purple-950/95 text-purple-200 border border-purple-500/60 text-[9px] font-black px-1.5 py-0.5 rounded shadow-lg z-10 flex items-center space-x-0.5">
          <span className="text-[7px] text-purple-400">x</span>
          <span className="text-white font-black">{item.quantity}</span>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />

        <div className="absolute bottom-0 inset-x-0 p-1.5 z-10 pointer-events-none">
          <p className="text-[9px] font-bold text-white line-clamp-1 group-hover:text-purple-300 transition-colors drop-shadow-md">
            {item.card.name}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between p-1 bg-slate-950/90 border-t border-slate-800/80">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.card.id, item.section);
          }}
          className="w-5 h-5 rounded bg-slate-900 border border-slate-800 text-slate-400 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/30 flex items-center justify-center transition-colors"
          title="Diminuir"
        >
          <i className="fas fa-minus text-[7px]"></i>
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview(item.card);
          }}
          className="p-1 text-[8px] text-slate-500 hover:text-purple-400 transition-colors"
          title="Ver Detalhes"
        >
          <i className="fas fa-eye text-[8px]"></i>
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAdd(item.card);
          }}
          className="w-5 h-5 rounded bg-slate-900 border border-slate-800 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/30 flex items-center justify-center transition-colors"
          title="Aumentar"
        >
          <i className="fas fa-plus text-[7px]"></i>
        </button>
      </div>
    </div>
  );
});

interface DeckListItemProps {
  item: DeckItem;
  showCostPills?: boolean;
  settings?: DeckBuilderSettings;
  onAdd: (card: Card) => void;
  onRemove: (cardId: string | number, section: string) => void;
  onPreview: (card: Card) => void;
}

const DeckListItem = React.memo<DeckListItemProps>(({
  item,
  showCostPills: explicitShowCostPills,
  settings,
  onAdd,
  onRemove,
  onPreview
}) => {
  const showCostPills = explicitShowCostPills ?? settings?.showCostPills ?? true;
  const costVal = getCardCostValue(item.card);
  return (
    <div
      onContextMenu={(e) => {
        e.preventDefault();
        onPreview(item.card);
      }}
      onClick={() => onPreview(item.card)}
      className="group relative flex items-center justify-between p-2 rounded-xl hover:bg-white/5 transition-all text-xs border border-transparent hover:border-white/5 cursor-pointer"
      title="Clique para ver detalhes"
    >
      <div className="flex items-center space-x-2 truncate">
        <div className="w-6 h-6 rounded overflow-hidden bg-slate-800 border border-slate-700 shrink-0 relative">
          <CardImage
            src={item.card.imageUrl}
            alt=""
            game={item.card.game}
            gameId={item.card.game_id}
            cardBackUrl={item.card.cardBackUrl || item.card.card_back_url}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex items-center space-x-1.5 truncate">
          {showCostPills && costVal !== 999 && (
            <span className="text-[8px] font-black text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1 py-0.2 rounded shrink-0">
              {costVal}
            </span>
          )}
          <span className="truncate text-slate-300 font-bold group-hover:text-white transition-colors text-[10px]">{item.card.name}</span>
        </div>
      </div>

      <div className="flex items-center space-x-2 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center space-x-1.5 bg-slate-950/60 p-0.5 rounded-lg border border-white/5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.card.id, item.section);
            }}
            className="w-5 h-5 rounded-md flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
            title="Diminuir"
          >
            <i className="fas fa-minus text-[8px]"></i>
          </button>
          <span className="w-3 text-center font-black text-purple-400 text-[10px]">{item.quantity}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAdd(item.card);
            }}
            className="w-5 h-5 rounded-md flex items-center justify-center text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
            title="Aumentar"
          >
            <i className="fas fa-plus text-[8px]"></i>
          </button>
        </div>
      </div>
    </div>
  );
});

export const DeckBuilderPage: React.FC<DeckBuilderPageProps> = ({
  activeGame,
  setActiveGame,
  dbGames
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDeckId = searchParams.get('deckId') || searchParams.get('deck');

  // O usuário só consegue acessar o DeckBuilder se vier através de Meus Decks com um baralho selecionado
  useEffect(() => {
    if (!urlDeckId) {
      navigate('/meus-decks', { replace: true });
    }
  }, [urlDeckId, navigate]);
  const [deckName, setDeckName] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deckItems, setDeckItems] = useState<DeckItem[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('Main');
  const [format, setFormat] = useState('classic');
  const [banlist, setBanlist] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState<number | null>(null);
  const [searchSort, setSearchSort] = useState<string>('default');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const [expandedCard, setExpandedCard] = useState<Card | null>(null);

  const [savedDecks, setSavedDecks] = useState<any[]>([]);
  const [currentGame, setCurrentGame] = useState<any>(null);
  const [currentDeckId, setCurrentDeckId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [copiedPublicLink, setCopiedPublicLink] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string, type: 'success' | 'error' | 'warning' } | null>(null);

  const [showExportModal, setShowExportModal] = useState(false);
  const [copiedExport, setCopiedExport] = useState(false);
  const [exportText, setExportText] = useState('');

  const [showImportModal, setShowImportModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState<DeckBuilderSettings>(() => loadDeckBuilderSettings());
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const searchCache = React.useRef<Record<string, any>>({});
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const catalogContainerRef = React.useRef<HTMLDivElement | null>(null);

  const gameSlug = useMemo(() => getSlugByGame(currentGame?.slug || (activeGame === 'All' ? 'All' : String(activeGame))), [activeGame, currentGame]);
  const gameRules = useMemo(() => getRulesByGame(gameSlug), [gameSlug]);

  const leaderCardItem = useMemo(() => {
    return deckItems.find(i => {
      if (!i || !i.card) return false;
      const section = String(i.section || '').toLowerCase();
      const rarity = String(i.card.rarity || i.card.set_rarity || '').toUpperCase().trim();
      const typeStr = String(i.card.type || i.card.cardType || i.card.card_type || i.card.category || i.card.supertype || '').toLowerCase();

      return (
        section === 'leader' ||
        section === 'líder' ||
        section === 'avatar' ||
        section === 'legend' ||
        section === 'hero' ||
        section === 'character' ||
        section === 'monster' ||
        section === 'oshi' ||
        rarity === 'L' ||
        rarity === 'LEADER' ||
        typeStr.includes('leader') ||
        typeStr.includes('avatar') ||
        typeStr.includes('hero') ||
        typeStr.includes('oshi') ||
        (i.card.life !== undefined && i.card.life !== null && Number(i.card.life) > 0 && gameSlug === 'one-piece')
      );
    }) || deckItems[0];
  }, [deckItems, gameSlug]);
  const leaderImageSrc = leaderCardItem?.card?.imageUrl || leaderCardItem?.card?.image_url;

  const handleExport = () => {
    if (deckItems.length === 0) {
      setSaveMessage({ text: "Adicione cartas ao baralho antes de exportar.", type: 'error' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    let text = '';
    if (gameSlug === 'one-piece') {
      text = generateOnePieceDeckText(deckItems);
    } else {
      text = generateGenericDeckText(deckItems, deckName);
    }

    setExportText(text);
    setCopiedExport(false);
    setShowExportModal(true);
  };

  const handleCopyExportText = () => {
    navigator.clipboard.writeText(exportText);
    setCopiedExport(true);
    setTimeout(() => setCopiedExport(false), 2500);
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = (deckName || 'baralho').toLowerCase().replace(/[^a-z0-9_-]/gi, '_') + '.txt';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportComplete = (
    importedItems: DeckItem[],
    replaceCurrent: boolean,
    importedDeckName?: string,
    notFoundItems?: any[]
  ) => {
    if (importedDeckName && (!deckName.trim() || replaceCurrent)) {
      setDeckName(importedDeckName);
    }

    setDeckItems(prev => {
      const base: DeckItem[] = replaceCurrent ? [] : [...prev];
      for (const item of importedItems) {
        const existingIdx = base.findIndex(
          i => (i.card.id === item.card.id || (i.card.code && item.card.code && i.card.code === item.card.code)) && i.section === item.section
        );
        if (existingIdx >= 0) {
          base[existingIdx] = {
            ...base[existingIdx],
            quantity: base[existingIdx].quantity + item.quantity
          };
        } else {
          base.push({ ...item });
        }
      }
      return base;
    });

    const totalImported = importedItems.reduce((acc, curr) => acc + curr.quantity, 0);
    if (notFoundItems && notFoundItems.length > 0) {
      setSaveMessage({
        text: `${totalImported} ${totalImported === 1 ? 'carta adicionada' : 'cartas adicionadas'} ao baralho (${notFoundItems.length} faltante(s) no catálogo)`,
        type: 'warning'
      });
    } else {
      setSaveMessage({
        text: `${totalImported} ${totalImported === 1 ? 'carta importada' : 'cartas importadas'} com sucesso!`,
        type: 'success'
      });
    }
    setTimeout(() => setSaveMessage(null), 4500);
  };

  const [dbFormats, setDbFormats] = useState<any[]>([]);

  useEffect(() => {
    const fetchGameInfoAndFormats = async () => {
      if (activeGame === 'All') {
        setCurrentGame(null);
        setDbFormats([]);
        return;
      }
      try {
        const games = await getCardgames();
        const game = games.find((g: any) => Number(g.id) === Number(activeGame));
        setCurrentGame(game);

        const formats = await getGameFormats(activeGame);
        setDbFormats(formats || []);
      } catch (err) {
        console.warn('Error fetching game_formats in DeckBuilder:', err);
        setDbFormats([]);
      }
    };
    fetchGameInfoAndFormats();
  }, [activeGame]);

  const availableFormatOptions = useMemo<DeckFormatOption[]>(() => {
    if (dbFormats && dbFormats.length > 0) {
      return dbFormats.map(f => ({
        id: f.id,
        name: f.name,
        slug: f.slug || f.name.toLowerCase().replace(/\s+/g, '-'),
        description: f.description || null
      }));
    }

    if (gameRules && Object.keys(gameRules.formats).length > 0) {
      return Object.keys(gameRules.formats).map(key => {
        const rule = gameRules.formats[key];
        return {
          name: key.charAt(0).toUpperCase() + key.slice(1),
          slug: key,
          description: rule?.observations || null
        };
      });
    }

    return [{ name: 'Classic', slug: 'classic', description: null }];
  }, [dbFormats, gameRules]);

  const availableFormats = useMemo(() => {
    return availableFormatOptions.map(f => f.slug || f.name);
  }, [availableFormatOptions]);

  const currentFormatOption = useMemo(() => {
    if (!format) return availableFormatOptions[0] || null;
    const norm = format.toLowerCase().trim();
    return (
      availableFormatOptions.find(f =>
        (f.slug && f.slug.toLowerCase() === norm) ||
        (f.name && f.name.toLowerCase() === norm) ||
        String(f.id) === String(format)
      ) || availableFormatOptions[0] || null
    );
  }, [availableFormatOptions, format]);

  // If format is not valid for game, reset to first available
  useEffect(() => {
    if (availableFormatOptions.length === 0) return;
    const isMatch = availableFormatOptions.some(f =>
      (f.slug && f.slug.toLowerCase() === format.toLowerCase()) ||
      (f.name && f.name.toLowerCase() === format.toLowerCase()) ||
      String(f.id) === String(format)
    );
    if (!isMatch && availableFormatOptions[0]) {
      setFormat(availableFormatOptions[0].slug || availableFormatOptions[0].name);
    }
  }, [availableFormatOptions, format]);

  const currentFormatRules = useMemo(() => {
    if (!gameRules) return null;

    // 1. Direct rule key match
    if (gameRules.formats[format]) return gameRules.formats[format];

    // 2. Match by currentFormatOption slug or name
    if (currentFormatOption) {
      if (currentFormatOption.slug && gameRules.formats[currentFormatOption.slug]) {
        return gameRules.formats[currentFormatOption.slug];
      }
      if (currentFormatOption.name && gameRules.formats[currentFormatOption.name]) {
        return gameRules.formats[currentFormatOption.name];
      }
    }

    // 3. Case-insensitive / slug-normalized match against gameRules.formats keys
    const formatSlug = (currentFormatOption?.slug || format || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const formatName = (currentFormatOption?.name || format || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    for (const [key, rule] of Object.entries(gameRules.formats)) {
      const keyNorm = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (keyNorm === formatSlug || keyNorm === formatName) {
        return rule;
      }
    }

    // 4. Fallback to classic/standard/first available format rule in gameRules
    return (
      gameRules.formats['classic'] ||
      gameRules.formats['standard'] ||
      gameRules.formats['competitive'] ||
      Object.values(gameRules.formats)[0] ||
      null
    );
  }, [gameRules, format, currentFormatOption]);

  // Sections based on game rules
  const getSections = useCallback(() => {
    if (currentFormatRules) {
      return currentFormatRules.deckSlots.map(slot => slot.name);
    }
    // Fallback
    const gameName = currentGame?.name || '';
    switch (gameName) {
      case 'Digimon': return ['Eggs', 'Main', 'Side', 'Tamer'];
      case 'Pokémon': return ['Pokémon', 'Trainer', 'Energy'];
      case 'Yu-Gi-Oh!': return ['Main', 'Extra', 'Side'];
      case 'One Piece': return ['Leader', 'Main'];
      case 'Grand Archive': return ['Material Deck', 'Main Deck', 'Sideboard'];
      case 'Flesh and Blood': return ['Hero', 'Arena', 'Main'];
      case 'Cardfight!! Vanguard':
      case 'Cardfight Vanguard':
      case 'Vanguard': return ['Main', 'Ride', 'G'];
      case 'Godzilla': return ['Monster', 'Main'];
      default: return ['Main', 'Side'];
    }
  }, [activeGame, currentFormatRules, currentGame]);

  const sections = useMemo(() => getSections(), [getSections]);

  useEffect(() => {
    if (sections.length > 0 && !sections.includes(activeTab)) {
      setActiveTab(sections[0]);
      setCurrentPage(1);
    }
  }, [sections, activeTab]);

  // Tipos aceitos pelo slot/aba atual da regra de formato ou do TCG
  const slotAcceptedTypes = useMemo(() => {
    if (!currentFormatRules?.deckSlots) return null;
    const activeSlot = currentFormatRules.deckSlots.find(
      s => s.name.toLowerCase() === activeTab.toLowerCase()
    );
    if (activeSlot?.query && (activeSlot.query.qkey === 'type' || activeSlot.query.qkey === 'cardType')) {
      const vals = Array.isArray(activeSlot.query.qval) ? activeSlot.query.qval : [activeSlot.query.qval];
      return vals.map(v => v.trim());
    }

    // Fallbacks para jogos com regras especiais de slots
    if (gameSlug === 'one-piece') {
      if (activeTab.toLowerCase().includes('leader') || activeTab.toLowerCase().includes('líder')) {
        return ['Leader'];
      } else {
        return ['Character', 'Event', 'Stage'];
      }
    }
    if (gameSlug === 'grand-archive' || gameSlug === 'grandarchive') {
      if (activeTab.toLowerCase().includes('material')) {
        return ['Champion', 'Regalia'];
      } else if (activeTab.toLowerCase().includes('main')) {
        return ['Ally', 'Action', 'Attack', 'Item', 'Weapon', 'Domain', 'Phantasia', 'Mastery'];
      }
    }
    if (gameSlug === 'hololive') {
      if (activeTab.toLowerCase() === 'oshi') return ['Oshi'];
      if (activeTab.toLowerCase() === 'cheer') return ['Cheer'];
      if (activeTab.toLowerCase() === 'main') return ['Holomem', 'Support'];
    }
    if (gameSlug === 'altered') {
      if (activeTab.toLowerCase() === 'hero') return ['Hero'];
      if (activeTab.toLowerCase() === 'main') return ['Character', 'Spell', 'Permanent'];
    }
    if (gameSlug === 'cyberpunk') {
      if (activeTab.toLowerCase() === 'legend') return ['Legend'];
      if (activeTab.toLowerCase() === 'main') return ['Gear', 'Program', 'Contact', 'Operation'];
    }
    if (gameSlug === 'godzilla') {
      if (activeTab.toLowerCase() === 'monster') return ['Monster'];
      if (activeTab.toLowerCase() === 'main') return ['Support', 'Event', 'Strategy'];
    }

    return null;
  }, [currentFormatRules, activeTab, gameSlug]);

  // Se o slot mudar, limpar filtros de tipo ativos que não pertençam ao novo slot
  useEffect(() => {
    if (slotAcceptedTypes && slotAcceptedTypes.length > 0) {
      const acceptedLowers = slotAcceptedTypes.map(t => t.toLowerCase());
      setActiveFilters(prev => {
        let changed = false;
        const next = { ...prev };
        if (next.type && !acceptedLowers.includes(next.type.toLowerCase())) {
          delete next.type;
          changed = true;
        }
        if (next.cardType && !acceptedLowers.includes(next.cardType.toLowerCase())) {
          delete next.cardType;
          changed = true;
        }
        return changed ? next : prev;
      });
    }
  }, [activeTab, slotAcceptedTypes]);

  const fetchCards = useCallback(async (query: string = '', page: number = 1, currentFilters: Record<string, string> = activeFilters) => {
    if (activeGame === 'All') return;

    // Check if game is available (sauce = 'homura' or 'kyubey')
    let gameInfo = currentGame;
    if (!gameInfo && dbGames) {
      gameInfo = dbGames.find((g: any) => Number(g.id) === Number(activeGame));
    }
    if (!gameInfo) {
      const games = await getCardgames();
      gameInfo = games.find((g: any) => Number(g.id) === Number(activeGame));
    }
    if (!gameInfo || (gameInfo.sauce !== 'homura' && gameInfo.sauce !== 'kyubey')) {
      setCards([]);
      setTotalPages(1);
      setTotalResults(0);
      return;
    }

    const filterParams: Record<string, string> = { ...currentFilters };
    const isOnePieceOrLeaderGame = gameSlug === 'one-piece' || !gameRules || activeTab.toLowerCase().includes('leader') || activeTab.toLowerCase().includes('líder');
    const isLeaderTab = (activeTab.toLowerCase().includes('leader') || activeTab.toLowerCase().includes('líder') || activeTab.toLowerCase() === 'hero') && isOnePieceOrLeaderGame;

    // For Sorcery or slot rules with specific queries, apply slot query if available
    const activeSlotRule = currentFormatRules?.deckSlots.find(s => s.name.toLowerCase() === activeTab.toLowerCase());
    if (activeSlotRule?.query) {
      const qk = activeSlotRule.query.qkey;
      const qv = activeSlotRule.query.qval;
      const vals = Array.isArray(qv) ? qv : [qv];
      // Se o usuário selecionou um filtro específico de tipo contido nos aceitos, preserva; caso contrário envia todos os tipos aceitos
      if (currentFilters[qk]) {
        filterParams[qk] = currentFilters[qk];
      } else {
        filterParams[qk] = vals.join(',');
      }
    } else if (isLeaderTab) {
      filterParams.rarity = 'L';
      if (!currentFilters.type) filterParams.type = 'Leader';
    } else if (slotAcceptedTypes && slotAcceptedTypes.length > 0) {
      const qk = (activeSlotRule?.query?.qkey === 'cardType') ? 'cardType' : 'type';
      if (!currentFilters[qk]) {
        filterParams[qk] = slotAcceptedTypes.join(',');
      }
    }

    // Exclusões explícitas de tipos para abas principais (ex: Material Deck cards não devem vir no Main Deck)
    if ((gameSlug === 'grand-archive' || gameSlug === 'grandarchive') && activeTab.toLowerCase().includes('main')) {
      if (!filterParams.type) {
        filterParams.excludeType = 'Champion,Regalia';
      }
    } else if (gameSlug === 'one-piece' && !isLeaderTab) {
      if (!filterParams.type && !filterParams.rarity) {
        filterParams.excludeType = 'Leader';
      }
    } else if (gameSlug === 'hololive' && activeTab.toLowerCase() === 'main') {
      if (!filterParams.type) {
        filterParams.excludeType = 'Oshi,Cheer';
      }
    } else if (gameSlug === 'altered' && activeTab.toLowerCase() === 'main') {
      if (!filterParams.type) {
        filterParams.excludeType = 'Hero';
      }
    } else if (gameSlug === 'cyberpunk' && activeTab.toLowerCase() === 'main') {
      if (!filterParams.type) {
        filterParams.excludeType = 'Legend';
      }
    } else if (gameSlug === 'godzilla' && activeTab.toLowerCase() === 'main') {
      if (!filterParams.type) {
        filterParams.excludeType = 'Monster';
      }
    }

    if (searchSort && searchSort !== 'default') {
      const [s, o] = searchSort.split('-');
      filterParams.sort = s;
      filterParams.order = o;
    }

    const filtersKey = JSON.stringify(filterParams);
    const cacheKey = `${activeGame}-${query}-${page}-${filtersKey}-${activeTab}`;
    if (searchCache.current[cacheKey]) {
      setCards(searchCache.current[cacheKey].data);
      setTotalPages(searchCache.current[cacheKey].totalPages);
      setTotalResults(searchCache.current[cacheKey].total ?? searchCache.current[cacheKey].data.length);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const response = await searchExternalCards(gameInfo.slug || gameInfo.name, query, page, 24, filterParams);
      let list = response.data || [];

      // For One Piece and leader-specific games, apply strict section rarity rules as safety net:
      if (gameSlug === 'one-piece') {
        const isLeader = (c: any) => {
          if (!c) return false;
          const r = String(c.rarity || c.set_rarity || '').toUpperCase().trim();
          if (r === 'L' || r === 'LEADER') return true;
          const t = String(c.cardType || c.card_type || c.type || c.category || c.type_name || c.supertype || '').toLowerCase().trim();
          if (t === 'leader' || t.includes('leader') || t.includes('líder')) return true;
          if (c.life !== undefined && c.life !== null && Number(c.life) > 0) return true;
          const n = String(c.name || '').toLowerCase();
          if (n.includes('(l)') || n.includes('[l]') || n.includes('(leader)')) return true;
          return false;
        };

        if (isLeaderTab) {
          list = list.filter((c: any) => isLeader(c));
        } else {
          list = list.filter((c: any) => !isLeader(c));
        }
      } else if (gameSlug === 'sorcery') {
        // Sorcery section matching
        const getSorceryType = (c: any) => {
          return String(c.cardType || c.type || c.guardian?.type || c.category || '').toLowerCase().trim();
        };
        if (activeTab.toLowerCase() === 'avatar') {
          list = list.filter((c: any) => getSorceryType(c).includes('avatar'));
        } else if (activeTab.toLowerCase() === 'atlas') {
          list = list.filter((c: any) => getSorceryType(c).includes('site'));
        } else if (activeTab.toLowerCase() === 'spellbook') {
          list = list.filter((c: any) => !getSorceryType(c).includes('avatar') && !getSorceryType(c).includes('site'));
        }
      } else if (gameSlug === 'grand-archive' || gameSlug === 'grandarchive') {
        // Grand Archive section filtering (Material Deck: Champion/Regalia/Memory cost; Main Deck: others/Reserve cost)
        const isMaterialCard = (c: any) => {
          const t = String(c.cardType || c.card_type || c.type || c.category || c.type_name || c.supertype || '').toLowerCase().trim();
          const costColor = String(c.cost_color || c.costType || c.cost || c.color || '').toLowerCase().trim();
          return t.includes('champion') || t.includes('regalia') || t.includes('campeão') || costColor.includes('memory') || costColor.includes('blue');
        };
        if (activeTab.toLowerCase().includes('material')) {
          list = list.filter((c: any) => isMaterialCard(c));
        } else if (activeTab.toLowerCase().includes('main')) {
          list = list.filter((c: any) => !isMaterialCard(c));
        }
      } else if (gameSlug === 'godzilla') {
        const isMonster = (c: any) => {
          const t = String(c.type || c.cardType || c.card_type || c.category || '').toLowerCase().trim();
          return t === 'monster' || t.includes('monster');
        };
        if (activeTab.toLowerCase() === 'monster') {
          list = list.filter((c: any) => isMonster(c));
        }
      } else if (gameSlug === 'hololive') {
        const isOshi = (c: any) => String(c.type || c.cardType || '').toLowerCase().includes('oshi');
        const isCheer = (c: any) => String(c.type || c.cardType || '').toLowerCase().includes('cheer');
        if (activeTab.toLowerCase() === 'oshi') {
          list = list.filter((c: any) => isOshi(c));
        } else if (activeTab.toLowerCase() === 'cheer') {
          list = list.filter((c: any) => isCheer(c));
        } else if (activeTab.toLowerCase() === 'main') {
          list = list.filter((c: any) => !isOshi(c) && !isCheer(c));
        }
      } else if (gameSlug === 'altered') {
        const isHero = (c: any) => String(c.type || c.cardType || '').toLowerCase().includes('hero');
        if (activeTab.toLowerCase() === 'hero') {
          list = list.filter((c: any) => isHero(c));
        } else if (activeTab.toLowerCase() === 'main') {
          list = list.filter((c: any) => !isHero(c));
        }
      } else if (gameSlug === 'cyberpunk') {
        const isLegend = (c: any) => String(c.type || c.cardType || '').toLowerCase().includes('legend');
        if (activeTab.toLowerCase() === 'legend') {
          list = list.filter((c: any) => isLegend(c));
        } else if (activeTab.toLowerCase() === 'main') {
          list = list.filter((c: any) => !isLegend(c));
        }
      }

      const finalTotal = response.total !== undefined ? response.total : list.length;
      const totalP = response.totalPages || Math.ceil(finalTotal / 24) || 1;
      const filteredResponse = { ...response, data: list, total: finalTotal, totalPages: totalP };
      searchCache.current[cacheKey] = filteredResponse;
      setCards(list);
      setTotalPages(totalP);
      setTotalResults(finalTotal);
      if (totalP > 0 && page > totalP) {
        setCurrentPage(1);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error("Search failed:", error);
        setTotalResults(0);
      }
    } finally {
      setLoading(false);
    }
  }, [activeGame, currentGame, dbGames, searchSort, activeFilters, activeTab, gameSlug, gameRules, currentFormatRules, slotAcceptedTypes]);

  const loadSavedDecks = useCallback(async () => {
    if (!user) {
      setSavedDecks([]);
      return;
    }
    try {
      const numericId = activeGame !== 'All' ? activeGame : undefined;
      const decks = await listUserDecks(numericId ? String(numericId) : undefined);
      setSavedDecks(decks);
    } catch (error) {
      console.error("Error loading decks:", error);
    }
  }, [user, activeGame]);

  // Carregar decks salvos do usuário ao mudar usuário ou jogo
  useEffect(() => {
    loadSavedDecks();
  }, [loadSavedDecks]);

  // Reset filters on game change
  useEffect(() => {
    setActiveFilters({});
    setSearchSort('default');
    setTotalResults(null);
  }, [activeGame]);

  // Buscar cartas do catálogo ao mudar jogo, filtros, ordenação, página ou aba
  useEffect(() => {
    fetchCards(searchQuery, currentPage, activeFilters);
  }, [activeGame, fetchCards, currentPage, activeFilters, searchSort, activeTab]);

  const handleLoadDeck = async (deckId: string) => {
    if (!deckId) return;
    try {
      setLoading(true);
      const deck = await getUserDeck(deckId);
      if (deck) {
        setDeckName(deck.name);
        const rawItems: DeckItem[] = deck.data.items || [];
        // Consolidate any duplicate entries from previous saves
        const consolidated: DeckItem[] = [];
        for (const item of rawItems) {
          const idx = consolidated.findIndex(
            i => (i.card.id === item.card.id || (i.card.code && item.card.code && i.card.code === item.card.code)) && i.section === item.section
          );
          if (idx >= 0) {
            consolidated[idx] = {
              ...consolidated[idx],
              quantity: consolidated[idx].quantity + item.quantity
            };
          } else {
            consolidated.push({ ...item });
          }
        }
        setDeckItems(consolidated);
        const loadedFormat = deck.game_format_id
          ? String(deck.game_format_id)
          : (deck.data?.format_slug || deck.data?.format || (deck.data?.format_id ? String(deck.data.format_id) : 'classic'));
        setFormat(loadedFormat);
        setIsPublic(deck.is_public);
        setCurrentDeckId(deck.id);

        if (deck.game_id && setActiveGame && Number(deck.game_id) !== Number(activeGame)) {
          setActiveGame(Number(deck.game_id));
        }
      } else {
        showNotification("Baralho não encontrado. Redirecionando para Meus Decks...", "error");
        navigate('/meus-decks', { replace: true });
      }
    } catch (error) {
      console.error("Error loading deck:", error);
      showNotification("Erro ao carregar o baralho.", "error");
      navigate('/meus-decks', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  // Carregar automaticamente o baralho da URL (?deckId=...)
  useEffect(() => {
    if (urlDeckId && urlDeckId !== currentDeckId) {
      handleLoadDeck(urlDeckId);
    }
  }, [urlDeckId]);

  const handleSaveDeck = async () => {
    if (!deckName.trim()) {
      setSaveMessage({ text: "Dê um nome ao seu baralho!", type: 'error' });
      return;
    }
    if (deckItems.length === 0) {
      setSaveMessage({ text: "Adicione cartas ao baralho antes de salvar.", type: 'error' });
      return;
    }

    if (gameSlug === 'godzilla' && godzillaDeckStats?.hasColorConflict) {
      setSaveMessage({ text: "Baralho inválido: não é permitido misturar cartas Vermelhas e Azuis em Godzilla.", type: 'error' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);
    try {
      const formatIdNumber = currentFormatOption?.id ? Number(currentFormatOption.id) : null;

      const deckData = {
        items: deckItems,
        format: currentFormatOption?.slug || format || 'classic',
        format_id: formatIdNumber,
        format_name: currentFormatOption?.name || format,
        format_slug: currentFormatOption?.slug || format,
        total: deckItems.reduce((acc, curr) => acc + curr.quantity, 0)
      };

      const gameIdToSave = !isNaN(Number(activeGame)) && Number(activeGame) > 0
        ? String(activeGame)
        : (currentGame?.id ? String(currentGame.id) : (gameRules?.id ? String(gameRules.id) : '0'));

      const id = await saveDeck(
        deckName,
        gameIdToSave,
        deckData,
        isPublic,
        currentDeckId || undefined,
        formatIdNumber
      );

      if (id) {
        setSaveMessage({ text: "Baralho salvo com sucesso!", type: 'success' });
        setCurrentDeckId(id as string);
        setSearchParams({ deckId: String(id) }, { replace: true });
        loadSavedDecks();

        // Log action
        await saveActionLog(
          currentDeckId ? 'update_deck' : 'create_deck',
          'user_decks',
          id as string,
          {
            name: deckName,
            game: activeGame,
            message: currentDeckId
              ? `atualizou o baralho "${deckName}" de ${activeGame}`
              : `criou um novo baralho: "${deckName}" para ${activeGame}`
          }
        );

        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (error) {
      setSaveMessage({ text: "Erro ao salvar baralho.", type: 'error' });
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDeck = () => {
    if (!currentDeckId) return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteDeck = async () => {
    if (!currentDeckId) return;
    try {
      await deleteUserDeck(currentDeckId);

      // Log action
      await saveActionLog('delete_deck', 'user_decks', currentDeckId, {
        name: deckName,
        message: `excluiu o baralho "${deckName}"`
      });

      setCurrentDeckId(null);
      setDeckName('');
      setDeckItems([]);
      setConfirmDeleteOpen(false);
      showNotification("Baralho excluído com sucesso.", "success");
      navigate('/meus-decks', { replace: true });
    } catch (error) {
      console.error("Error deleting deck:", error);
      showNotification("Erro ao excluir baralho.", "error");
    }
  };

  const handleNewDeck = () => {
    setCurrentDeckId(null);
    setDeckName('');
    setDeckItems([]);
    setSaveMessage(null);
    setSearchParams({}, { replace: true });
  };

  const handlePreviewCard = useCallback((card: Card) => {
    setExpandedCard(card);
  }, []);

  const addToDeck = useCallback((card: Card) => {
    const isOnePieceOrLeaderGame = gameSlug === 'one-piece' || !gameRules || activeTab.toLowerCase().includes('leader') || activeTab.toLowerCase().includes('líder');
    const isLeaderTab = (activeTab.toLowerCase().includes('leader') || activeTab.toLowerCase().includes('líder') || activeTab.toLowerCase() === 'hero') && isOnePieceOrLeaderGame;
    const rarity = String(card.rarity || card.set_rarity || '').toUpperCase().trim();
    const typeStr = String(card.cardType || card.card_type || card.type || card.guardian?.type || card.category || card.type_name || card.supertype || '').toLowerCase().trim();

    if (gameSlug === 'one-piece') {
      const isLeaderCard = rarity === 'L' ||
        rarity === 'LEADER' ||
        typeStr === 'leader' ||
        typeStr.includes('leader') ||
        typeStr.includes('líder') ||
        (card.life !== undefined && card.life !== null && Number(card.life) > 0) ||
        /\(L\)$/i.test(card.name || '') ||
        /\[L\]$/i.test(card.name || '');

      // Rarity L rule: Leader tab ONLY accepts rarity L cards; Main / non-leader tabs accept everything EXCEPT rarity L cards
      if (isLeaderTab && !isLeaderCard) {
        return;
      }
      if (!isLeaderTab && isLeaderCard) {
        return;
      }
    } else if (gameSlug === 'sorcery') {
      // Sorcery section checks
      if (activeTab.toLowerCase() === 'avatar' && !typeStr.includes('avatar')) {
        return;
      }
      if (activeTab.toLowerCase() === 'atlas' && !typeStr.includes('site')) {
        return;
      }
      if (activeTab.toLowerCase() === 'spellbook' && (typeStr.includes('avatar') || typeStr.includes('site'))) {
        return;
      }
    } else if (gameSlug === 'star-wars' || gameSlug === '8' || gameSlug.includes('star-wars') || gameSlug.includes('swu')) {
      const isLeader = typeStr.includes('leader') || typeStr.includes('líder');
      const isBase = typeStr.includes('base');
      if (activeTab.toLowerCase().includes('leader') && !isLeader) {
        return;
      }
      if (activeTab.toLowerCase().includes('base') && !isBase) {
        return;
      }
      if (!activeTab.toLowerCase().includes('leader') && !activeTab.toLowerCase().includes('base') && (isLeader || isBase)) {
        return;
      }
    } else if (gameSlug === 'grand-archive' || gameSlug === 'grandarchive') {
      const isMaterialCard = typeStr.includes('champion') ||
        typeStr.includes('regalia') ||
        typeStr.includes('campeão') ||
        String((card as any).cost_color || (card as any).costType || (card as any).color || '').toLowerCase().includes('blue') ||
        String((card as any).cost_color || (card as any).costType || (card as any).color || '').toLowerCase().includes('memory');

      if (activeTab.toLowerCase().includes('material')) {
        if (!isMaterialCard) {
          showNotification('Apenas Campeões e Regalias (Custo Azul) podem ser adicionados ao Material Deck.', 'error');
          return;
        }
      } else if (activeTab.toLowerCase().includes('main')) {
        if (isMaterialCard) {
          showNotification('Campeões e Regalias não podem ser adicionados ao Main Deck.', 'error');
          return;
        }
      }
    } else if (gameSlug === 'godzilla') {
      const isMonster = typeStr === 'monster' || typeStr.includes('monster');
      const isMonsterTab = activeTab.toLowerCase() === 'monster';
      const isMainTab = activeTab.toLowerCase() === 'main';

      if (isMonsterTab) {
        if (!isMonster) {
          showNotification('Apenas cartas do tipo Monster podem ser adicionadas ao Monster Deck.', 'error');
          return;
        }
        const rank = getGodzillaMonsterRank(card);
        if (!rank) {
          showNotification('Apenas Monstros de Rank I, II, III ou IV podem ser adicionados ao Monster Deck.', 'error');
          return;
        }
      }

      // Main Deck Invade 2 Limit (Max 10)
      if (isMainTab && isGodzillaInvade2(card)) {
        const currentInvade2 = deckItems
          .filter(i => i.section.toLowerCase() === 'main' && isGodzillaInvade2(i.card))
          .reduce((acc, curr) => acc + curr.quantity, 0);
        if (currentInvade2 >= 10) {
          showNotification('O Main Deck permite no máximo 10 cartas com Ícone de Invasão 2.', 'error');
          return;
        }
      }

      // Color restriction: White + either Red or Blue (Red and Blue cannot mix)
      const cardColor = getGodzillaColor(card);
      if (cardColor === 'red' || cardColor === 'blue') {
        const hasOpposite = deckItems.some(item => {
          const itemColor = getGodzillaColor(item.card);
          return (cardColor === 'red' && itemColor === 'blue') || (cardColor === 'blue' && itemColor === 'red');
        });
        if (hasOpposite) {
          showNotification('Baralhos de Godzilla não podem misturar cartas Vermelhas e Azuis (use apenas Branco + Vermelho ou Branco + Azul).', 'error');
          return;
        }
      }

      // Max copies across all sections based on base card code (ESD01-001 and ESD01-001+ count as same card)
      const baseCode = getGodzillaBaseCode(card);
      const totalBaseCopies = deckItems
        .filter(item => {
          const itemBase = getGodzillaBaseCode(item.card);
          return baseCode && itemBase ? itemBase === baseCode : item.card.id === card.id;
        })
        .reduce((acc, curr) => acc + curr.quantity, 0);

      if (totalBaseCopies >= 4) {
        showNotification(`Limite de 4 cópias atingido para este card (${card.code || card.name}). Cartas com '+' contam para a mesma numeração.`, 'error');
        return;
      }
    } else if (gameSlug === 'hololive') {
      const isOshi = typeStr.includes('oshi');
      const isCheer = typeStr.includes('cheer');
      if (activeTab.toLowerCase() === 'oshi' && !isOshi) {
        showNotification('Apenas cartas do tipo Oshi podem ser adicionadas ao slot Oshi.', 'error');
        return;
      }
      if (activeTab.toLowerCase() === 'cheer' && !isCheer) {
        showNotification('Apenas cartas do tipo Cheer podem ser adicionadas ao Cheer Deck.', 'error');
        return;
      }
      if (activeTab.toLowerCase() === 'main' && (isOshi || isCheer)) {
        showNotification('Cartas Oshi e Cheer não pertencem ao Main Deck.', 'error');
        return;
      }
    } else if (gameSlug === 'altered') {
      const isHero = typeStr.includes('hero');
      if (activeTab.toLowerCase() === 'hero' && !isHero) {
        showNotification('Apenas cartas do tipo Hero podem ser adicionadas ao slot Hero.', 'error');
        return;
      }
      if (activeTab.toLowerCase() !== 'hero' && isHero) {
        showNotification('Cartas do tipo Hero só podem ser adicionadas ao slot Hero.', 'error');
        return;
      }
    } else if (gameSlug === 'cyberpunk') {
      const isLegend = typeStr.includes('legend');
      if (activeTab.toLowerCase() === 'legend' && !isLegend) {
        showNotification('Apenas cartas do tipo Legend podem ser adicionadas ao slot Legend.', 'error');
        return;
      }
      if (activeTab.toLowerCase() !== 'legend' && isLegend) {
        showNotification('Cartas do tipo Legend só podem ser adicionadas ao slot Legend.', 'error');
        return;
      }
    } else if (gameSlug === 'grand-archive' || gameSlug === 'grandarchive') {
      const isMaterial = typeStr.includes('champion') || typeStr.includes('regalia') || typeStr.includes('campeão');
      if (activeTab.toLowerCase().includes('material') && !isMaterial) {
        showNotification('Apenas cartas do tipo Champion ou Regalia podem ser adicionadas ao Material Deck.', 'error');
        return;
      }
      if (activeTab.toLowerCase().includes('main') && isMaterial) {
        showNotification('Cartas Champion e Regalia pertencem exclusivamente ao Material Deck.', 'error');
        return;
      }
    } else if (slotAcceptedTypes && slotAcceptedTypes.length > 0) {
      const cardTypeStr = String(card.type || card.cardType || card.card_type || '').toLowerCase();
      const cardTypesArr = Array.isArray(card.types) ? card.types.map((t: any) => String(t).toLowerCase()) : [];
      const isAccepted = slotAcceptedTypes.some(acc => {
        const accLow = acc.toLowerCase();
        return cardTypeStr.includes(accLow) || cardTypesArr.some(t => t.includes(accLow));
      });
      if (!isAccepted) {
        showNotification(`Apenas cartas do tipo ${slotAcceptedTypes.join(' ou ')} podem ser adicionadas ao slot ${activeTab}.`, 'error');
        return;
      }
    }

    // Determine slot max if defined in current format rules (e.g., Leader slot max: 1)
    const slotRule = currentFormatRules?.deckSlots.find(s => s.name.toLowerCase() === activeTab.toLowerCase());
    const slotMax = slotRule ? slotRule.max : (isLeaderTab ? 1 : undefined);

    // Determine max copies
    let maxCopies = 4;
    if (currentFormatRules) {
      if (gameSlug === 'sorcery') {
        maxCopies = sorceryMax(card.rarity || 'Ordinary');
      } else if ((gameSlug === 'grand-archive' || gameSlug === 'grandarchive') && activeTab.toLowerCase().includes('material')) {
        // Material Deck is strictly singleton: only 1 card of each name
        maxCopies = 1;
      } else {
        maxCopies = currentFormatRules.maxCopies || 4;
      }
    }

    setDeckItems(prev => {
      // For Godzilla Monster section: exactly 1 card of each Rank (I, II, III, IV)
      if (gameSlug === 'godzilla' && activeTab.toLowerCase() === 'monster') {
        const targetRank = getGodzillaMonsterRank(card);
        const existingSameRank = prev.find(item => item.section.toLowerCase() === 'monster' && getGodzillaMonsterRank(item.card) === targetRank);
        if (existingSameRank) {
          const withoutSameRank = prev.filter(item => item !== existingSameRank);
          return [...withoutSameRank, { card, quantity: 1, section: activeTab }];
        }
        const monsterCount = prev.filter(item => item.section.toLowerCase() === 'monster').reduce((acc, curr) => acc + curr.quantity, 0);
        if (monsterCount >= 4) {
          return prev;
        }
        return [...prev, { card, quantity: 1, section: activeTab }];
      }

      // Check count in active section
      const sectionCount = prev
        .filter(item => item.section === activeTab)
        .reduce((acc, curr) => acc + curr.quantity, 0);

      if (slotMax !== undefined && sectionCount >= slotMax) {
        if (slotMax === 1) {
          // Replace existing card in 1-slot sections (like Leader)
          const withoutLeader = prev.filter(item => item.section !== activeTab);
          return [...withoutLeader, { card, quantity: 1, section: activeTab }];
        }
        return prev;
      }

      const totalCount = prev.filter(item => item.card.id === card.id).reduce((acc, curr) => acc + curr.quantity, 0);
      if (totalCount >= maxCopies) {
        return prev;
      }

      const existingInSection = prev.find(item => item.card.id === card.id && item.section === activeTab);
      if (existingInSection) {
        return prev.map(item => item.card.id === card.id && item.section === activeTab
          ? { ...item, quantity: item.quantity + 1 }
          : item
        );
      }
      return [...prev, { card, quantity: 1, section: activeTab }];
    });
  }, [activeTab, gameSlug, gameRules, currentFormatRules, showNotification, deckItems]);

  const removeFromDeck = useCallback((cardId: string | number, section: string) => {
    setDeckItems(prev => {
      const existing = prev.find(item => item.card.id === cardId && item.section === section);
      if (existing && existing.quantity > 1) {
        return prev.map(item => item.card.id === cardId && item.section === section
          ? { ...item, quantity: item.quantity - 1 }
          : item
        );
      }
      return prev.filter(item => !(item.card.id === cardId && item.section === section));
    });
  }, []);

  const clearDeck = () => {
    if (deckItems.length === 0) {
      showNotification('O baralho já está vazio.', 'info');
      return;
    }
    if (settings.confirmBeforeClear) {
      setConfirmClearOpen(true);
    } else {
      handleConfirmClearDeck();
    }
  };

  const handleConfirmClearDeck = () => {
    setDeckItems([]);
    setConfirmClearOpen(false);
    showNotification('Todas as cartas foram removidas do baralho.', 'success');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchCards(searchQuery, 1, activeFilters);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage === currentPage || newPage < 1 || (totalPages > 0 && newPage > totalPages)) return;
    setCurrentPage(newPage);
    catalogContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cardCountsMap = useMemo(() => {
    const map = new Map<string | number, number>();
    const baseCodeMap = new Map<string, number>();

    for (const item of deckItems) {
      if (item.card?.id !== undefined && item.card?.id !== null) {
        map.set(item.card.id, (map.get(item.card.id) || 0) + item.quantity);
      }
      if (gameSlug === 'godzilla') {
        const base = getGodzillaBaseCode(item.card);
        if (base) {
          baseCodeMap.set(base, (baseCodeMap.get(base) || 0) + item.quantity);
        }
      }
    }

    return {
      get: (id: string | number, card?: Card) => {
        if (gameSlug === 'godzilla' && card) {
          const base = getGodzillaBaseCode(card);
          if (base && baseCodeMap.has(base)) {
            return baseCodeMap.get(base) || 0;
          }
        }
        return map.get(id) || 0;
      }
    };
  }, [deckItems, gameSlug]);

  const sectionedDeckItems = useMemo(() => {
    const result: Array<{
      section: string;
      items: DeckItem[];
      totalCount: number;
      minCount?: number;
      maxCount?: number;
      isInvalid: boolean;
    }> = [];

    for (const section of sections) {
      const sectionItems = deckItems.filter(i => i.section === section);
      if (sectionItems.length === 0) continue;

      const totalCount = sectionItems.reduce((acc, curr) => acc + curr.quantity, 0);
      const slotRule = currentFormatRules?.deckSlots.find(s => s.name === section);
      const minCount = slotRule?.min;
      const maxCount = slotRule?.max;
      let isInvalid = slotRule
        ? (totalCount < (minCount || 0) || totalCount > (maxCount || 999))
        : false;

      // Special validation for Godzilla
      if (gameSlug === 'godzilla') {
        if (section.toLowerCase() === 'monster') {
          const ranks = new Set(sectionItems.map(i => getGodzillaMonsterRank(i.card)).filter((r): r is number => r !== null));
          if (totalCount !== 4 || ranks.size !== 4 || !ranks.has(1) || !ranks.has(2) || !ranks.has(3) || !ranks.has(4)) {
            isInvalid = true;
          }
        } else if (section.toLowerCase() === 'main') {
          const invade2Count = sectionItems
            .filter(i => isGodzillaInvade2(i.card))
            .reduce((acc, curr) => acc + curr.quantity, 0);
          if (totalCount !== 50 || invade2Count > 10) {
            isInvalid = true;
          }
        }
      }

      const sorted = [...sectionItems].sort((a, b) => {
        if (settings.deckSort === 'name') {
          return (a.card?.name || '').localeCompare(b.card?.name || '');
        }
        if (settings.deckSort === 'type') {
          const typeA = String(a.card?.cardType || a.card?.card_type || a.card?.type || a.card?.category || '');
          const typeB = String(b.card?.cardType || b.card?.card_type || b.card?.type || b.card?.category || '');
          const typeCompare = typeA.localeCompare(typeB);
          if (typeCompare !== 0) return typeCompare;
          return (a.card?.name || '').localeCompare(b.card?.name || '');
        }
        const costA = getCardCostValue(a.card);
        const costB = getCardCostValue(b.card);
        if (costA !== costB) return costA - costB;
        return (a.card?.name || '').localeCompare(b.card?.name || '');
      });

      result.push({
        section,
        items: sorted,
        totalCount,
        minCount,
        maxCount,
        isInvalid
      });
    }

    return result;
  }, [sections, deckItems, settings.deckSort, currentFormatRules, gameSlug]);

  // Godzilla deck rules tracker and status metrics
  const godzillaDeckStats = useMemo(() => {
    if (gameSlug !== 'godzilla') return null;

    const monsterItems = deckItems.filter(i => i.section.toLowerCase() === 'monster');
    const mainItems = deckItems.filter(i => i.section.toLowerCase() === 'main');

    const monsterCount = monsterItems.reduce((acc, curr) => acc + curr.quantity, 0);
    const mainCount = mainItems.reduce((acc, curr) => acc + curr.quantity, 0);

    const ranksPresent = new Set<number>();
    monsterItems.forEach(i => {
      const r = getGodzillaMonsterRank(i.card);
      if (r) ranksPresent.add(r);
    });

    const invade2Count = mainItems
      .filter(i => isGodzillaInvade2(i.card))
      .reduce((acc, curr) => acc + curr.quantity, 0);

    let hasRed = false;
    let hasBlue = false;
    let hasWhite = false;

    deckItems.forEach(i => {
      const col = getGodzillaColor(i.card);
      if (col === 'red') hasRed = true;
      if (col === 'blue') hasBlue = true;
      if (col === 'white') hasWhite = true;
    });

    const hasColorConflict = hasRed && hasBlue;
    const colorTheme = hasColorConflict
      ? 'Conflito (Vermelho e Azul)'
      : (hasRed && hasWhite ? 'Vermelho + Branco'
        : (hasBlue && hasWhite ? 'Azul + Branco'
          : (hasRed ? 'Mono Vermelho'
            : (hasBlue ? 'Mono Azul'
              : (hasWhite ? 'Branco' : 'Sem cartas com cor')))));

    const isMonsterDeckValid = monsterCount === 4 && ranksPresent.size === 4 && [1, 2, 3, 4].every(r => ranksPresent.has(r));
    const isMainDeckValid = mainCount === 50 && invade2Count <= 10;
    const isDeckValid = isMonsterDeckValid && isMainDeckValid && !hasColorConflict;

    return {
      monsterCount,
      mainCount,
      ranksPresent,
      invade2Count,
      hasColorConflict,
      colorTheme,
      isMonsterDeckValid,
      isMainDeckValid,
      isDeckValid
    };
  }, [deckItems, gameSlug]);

  if (activeGame === 'All' || !activeGame) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          onSelectGame={(id) => {
            if (setActiveGame) {
              setActiveGame(id as number);
            }
          }}
          title="Escolha um Jogo"
          subtitle="Para começar a construir e gerenciar seu deck, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  const totalCards = deckItems.reduce((acc, curr) => acc + curr.quantity, 0);
  const currentGameName = currentGame?.name || getGameNameById(activeGame);
  const currentGameIcon = getGameIcon(activeGame);

  if (!urlDeckId) {
    return <Navigate to="/meus-decks" replace />;
  }

  return (
    <div className="flex-1 mx-auto p-2 lg:p-4 w-full text-slate-200">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* COLUNA ESQUERDA (SIDEBAR & DECK LIST) */}
        <div className="w-full lg:w-[340px] flex-shrink-0 space-y-4">
          {/* Deck Info & Content Control Card */}
          <div className="bg-slate-900/40 p-4 rounded-[2rem] border border-slate-800 space-y-4 shadow-2xl backdrop-blur-md">
            {currentDeckId ? (
              <>
                {/* Header do Baralho em Edição */}
                <div className="flex items-center gap-3">
                  {leaderImageSrc ? (
                    <div
                      onClick={() => { if (leaderCardItem?.card) setExpandedCard(leaderCardItem.card); }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (leaderCardItem?.card) setExpandedCard(leaderCardItem.card);
                      }}
                      className="relative w-12 h-12 rounded-xl overflow-hidden bg-slate-950 border border-purple-500/40 shrink-0 shadow-lg cursor-pointer hover:border-purple-400 transition-colors"
                      title="Clique para ver detalhes do líder"
                    >
                      <CardImage
                        src={leaderImageSrc}
                        alt={leaderCardItem?.card?.name || ''}
                        game={activeGame}
                        gameId={activeGame}
                        className="w-full h-full object-cover object-top scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent pointer-events-none" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shrink-0 shadow-inner">
                      <i className={`fas ${getGameIcon(activeGame)} text-lg`}></i>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3
                        className="text-sm font-black text-white tracking-wide truncate"
                        title={deckName || 'Baralho sem nome'}
                      >
                        {deckName || 'Baralho sem nome'}
                      </h3>
                      {isPublic ? (
                        <span
                          className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[9px] font-bold shrink-0 flex items-center gap-1"
                          title="Baralho Público"
                        >
                          <i className="fas fa-globe text-[8px]"></i>
                          <span>Público</span>
                        </span>
                      ) : (
                        <span
                          className="px-1.5 py-0.5 rounded-md bg-slate-800/80 border border-slate-700/60 text-slate-400 text-[9px] font-bold shrink-0 flex items-center gap-1"
                          title="Baralho Privado"
                        >
                          <i className="fas fa-lock text-[8px]"></i>
                          <span>Privado</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 mt-0.5 truncate">
                      <span className="text-purple-300 font-bold truncate">{currentGameName}</span>
                      <span>•</span>
                      <span className="truncate">{currentFormatOption?.name || format}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    id="open-deckbuilder-settings-button"
                    onClick={() => setShowSettingsModal(true)}
                    title="Configurações do Construtor"
                    className="p-2.5 bg-slate-950 border border-slate-700 rounded-xl text-purple-300 hover:text-white hover:bg-purple-950/50 hover:border-purple-500/50 flex items-center justify-center transition-colors cursor-pointer shrink-0"
                  >
                    <i className="fas fa-cog text-xs"></i>
                  </button>
                </div>

                {/* Atalho para editar informações em Meus Decks */}
                <Link
                  to="/meus-decks"
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 text-purple-300 hover:text-purple-200 text-xs font-bold transition-all group shadow-sm active:scale-[0.99]"
                  title="Editar nome, formato ou visibilidade na página Meus Decks"
                >
                  <span className="flex items-center gap-2">
                    <i className="fas fa-sliders-h text-purple-400 text-[11px]"></i>
                    <span>Editar Informações do Baralho</span>
                  </span>
                  <span className="text-[10px] text-purple-400 group-hover:text-purple-300 flex items-center gap-1 font-semibold">
                    <span>Meus Decks</span>
                    <i className="fas fa-arrow-right text-[9px] group-hover:translate-x-0.5 transition-transform"></i>
                  </span>
                </Link>

                {/* Compartilhamento Público compacto quando ativo */}
                {isPublic && (
                  <div className="flex items-center gap-2 p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 text-emerald-300 text-[10px] font-mono truncate">
                      <i className="fas fa-globe text-[10px] text-emerald-400 shrink-0"></i>
                      <span className="truncate">/deck/{currentDeckId}</span>
                    </div>
                    <Link
                      to={`/deck/${currentDeckId}`}
                      target="_blank"
                      className="text-[9px] font-bold text-emerald-400 hover:text-emerald-300 bg-emerald-500/20 hover:bg-emerald-500/30 px-2 py-1 rounded-lg shrink-0 flex items-center gap-1 transition-colors"
                      title="Abrir página pública do baralho"
                    >
                      <span>Ver</span>
                      <i className="fas fa-external-link-alt text-[8px]"></i>
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        const fullUrl = `${window.location.origin}/deck/${currentDeckId}`;
                        navigator.clipboard.writeText(fullUrl);
                        setCopiedPublicLink(true);
                        setTimeout(() => setCopiedPublicLink(false), 2000);
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all shrink-0 border ${
                        copiedPublicLink
                          ? 'bg-emerald-600 text-white border-emerald-500'
                          : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border-emerald-500/30'
                      }`}
                      title="Copiar Link Público"
                    >
                      <i className={`fas ${copiedPublicLink ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Nenhum baralho selecionado */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Editor de Cartas</span>
                  <button
                    type="button"
                    id="open-deckbuilder-settings-button"
                    onClick={() => setShowSettingsModal(true)}
                    title="Configurações do Construtor"
                    className="p-2 bg-slate-950 border border-slate-700 rounded-xl text-purple-300 hover:text-white transition-colors"
                  >
                    <i className="fas fa-cog text-xs"></i>
                  </button>
                </div>

                <div className="p-3.5 rounded-2xl bg-purple-500/10 border border-purple-500/20 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-300 shrink-0 mt-0.5">
                      <i className="fas fa-layer-group text-xs"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-black text-white">Nenhum baralho aberto</h4>
                      <p className="text-[11px] text-slate-300/80 mt-0.5 leading-relaxed">
                        Esta tela é exclusiva para editar as cartas do baralho. Crie ou selecione um baralho em <strong>Meus Decks</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 pt-1">
                    <Link
                      to="/meus-decks?novo=1"
                      className="w-full py-2 px-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs text-center flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20 transition-all active:scale-95"
                    >
                      <i className="fas fa-plus text-[10px]"></i>
                      <span>Criar Novo Baralho</span>
                    </Link>

                    <Link
                      to="/meus-decks"
                      className="w-full py-2 px-3 rounded-xl bg-slate-950 hover:bg-slate-900 border border-slate-700 text-slate-200 font-bold text-xs text-center flex items-center justify-center gap-2 transition-all"
                    >
                      <i className="fas fa-folder-open text-[10px] text-purple-400"></i>
                      <span>Ir para Meus Decks</span>
                    </Link>
                  </div>
                </div>

                {savedDecks.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <span className="text-[10px] font-bold text-slate-400 px-1">Ou abra um baralho existente:</span>
                    <div className="relative">
                      <select
                        onChange={(e) => {
                          if (e.target.value) handleLoadDeck(e.target.value);
                        }}
                        defaultValue=""
                        className="w-full p-2.5 pr-8 rounded-xl bg-slate-950 border border-slate-700 text-xs font-bold tracking-wide outline-none focus:border-purple-500 transition-all appearance-none text-purple-300 hover:border-slate-600 cursor-pointer shadow-sm"
                      >
                        <option value="" disabled>-- Selecionar Baralho --</option>
                        {savedDecks.map(d => (
                          <option key={d.id} value={d.id} className="bg-slate-900 text-slate-100 py-1">
                            {d.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-purple-400">
                        <i className="fas fa-chevron-down text-[10px]"></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Ações Rápidas */}
            <div className="grid grid-cols-3 gap-2 py-1">
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl bg-slate-950/50 border border-white/5 hover:border-purple-500/50 hover:bg-slate-900 group transition-all"
              >
                <i className="fas fa-file-import text-[10px] text-slate-500 group-hover:text-purple-400"></i>
                <span className="text-[7px] font-black uppercase tracking-widest text-slate-600 group-hover:text-white">Importar</span>
              </button>
              <button
                onClick={handleExport}
                className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl bg-slate-950/50 border border-white/5 hover:border-blue-500/50 hover:bg-slate-900 group transition-all"
              >
                <i className="fas fa-file-export text-[10px] text-slate-500 group-hover:text-blue-400"></i>
                <span className="text-[7px] font-black uppercase tracking-widest text-slate-600 group-hover:text-white">Exportar</span>
              </button>
              <button onClick={clearDeck} className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl bg-slate-950/50 border border-white/5 hover:border-red-500/50 hover:bg-slate-900 group transition-all">
                <i className="fas fa-trash-alt text-[10px] text-slate-500 group-hover:text-red-400"></i>
                <span className="text-[7px] font-black uppercase tracking-widest text-slate-600 group-hover:text-white">Limpar</span>
              </button>
            </div>

            {(currentFormatOption?.description || currentFormatRules?.observations) && (
              <div className="bg-purple-600/10 border border-purple-500/20 p-3 rounded-xl">
                <p className="text-[8px] text-purple-300 font-bold uppercase tracking-wider leading-relaxed">
                  <i className="fas fa-info-circle mr-2"></i>
                  {currentFormatOption?.description || currentFormatRules?.observations}
                </p>
              </div>
            )}

            {godzillaDeckStats && (
              <div className="p-3.5 rounded-2xl bg-slate-950/70 border border-purple-500/20 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-purple-300 flex items-center gap-1.5">
                    <i className="fa-solid fa-radiation text-purple-400"></i>
                    Regras de Godzilla
                  </span>
                  <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md border ${godzillaDeckStats.isDeckValid
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                      : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                    }`}>
                    {godzillaDeckStats.isDeckValid ? 'Válido' : 'Pendente'}
                  </span>
                </div>

                <div className="space-y-1.5 text-[11px]">
                  {/* Monster Deck */}
                  <div className="flex items-center justify-between bg-slate-900/60 px-2.5 py-1.5 rounded-xl border border-slate-800">
                    <span className="text-slate-300 font-semibold flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${godzillaDeckStats.isMonsterDeckValid ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                      Monster Deck
                    </span>
                    <span className={`font-mono font-bold text-xs ${godzillaDeckStats.isMonsterDeckValid ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {godzillaDeckStats.monsterCount} / 4
                    </span>
                  </div>

                  {/* Badges de Rank */}
                  <div className="grid grid-cols-4 gap-1 pt-0.5">
                    {[1, 2, 3, 4].map(rank => {
                      const roman = rank === 1 ? 'I' : rank === 2 ? 'II' : rank === 3 ? 'III' : 'IV';
                      const hasRank = godzillaDeckStats.ranksPresent.has(rank);
                      return (
                        <div
                          key={rank}
                          className={`text-center py-1 rounded-lg text-[9px] font-black border transition-all ${hasRank
                              ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                              : 'bg-slate-900/80 text-slate-500 border-slate-800'
                            }`}
                          title={hasRank ? `Rank ${roman} adicionado` : `Falta Rank ${roman}`}
                        >
                          Rank {roman} {hasRank ? '✓' : ''}
                        </div>
                      );
                    })}
                  </div>

                  {/* Main Deck */}
                  <div className="flex items-center justify-between bg-slate-900/60 px-2.5 py-1.5 rounded-xl border border-slate-800">
                    <span className="text-slate-300 font-semibold flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${godzillaDeckStats.mainCount === 50 ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                      Main Deck
                    </span>
                    <span className={`font-mono font-bold text-xs ${godzillaDeckStats.mainCount === 50 ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {godzillaDeckStats.mainCount} / 50
                    </span>
                  </div>

                  {/* Invade 2 Limit */}
                  <div className="flex items-center justify-between bg-slate-900/60 px-2.5 py-1.5 rounded-xl border border-slate-800">
                    <span className="text-slate-300 font-semibold flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${godzillaDeckStats.invade2Count <= 10 ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                      Ícone Invade 2
                    </span>
                    <span className={`font-mono font-bold text-xs ${godzillaDeckStats.invade2Count <= 10 ? 'text-slate-300' : 'text-red-400 font-black'}`}>
                      {godzillaDeckStats.invade2Count} / 10
                    </span>
                  </div>

                  {/* Cores */}
                  <div className="flex items-center justify-between bg-slate-900/60 px-2.5 py-1.5 rounded-xl border border-slate-800">
                    <span className="text-slate-300 font-semibold">Cores</span>
                    <span className={`text-[10px] font-bold ${godzillaDeckStats.hasColorConflict ? 'text-red-400 font-black' : 'text-purple-300'}`}>
                      {godzillaDeckStats.colorTheme}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {saveMessage && (
              <div className={`p-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center space-x-2 ${saveMessage.type === 'success'
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : saveMessage.type === 'warning'
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                <i className={`fas ${saveMessage.type === 'success'
                    ? 'fa-check-circle'
                    : saveMessage.type === 'warning'
                      ? 'fa-exclamation-triangle'
                      : 'fa-exclamation-circle'
                  }`}></i>
                <span>{saveMessage.text}</span>
              </div>
            )}

            {currentDeckId ? (
              <button
                onClick={handleSaveDeck}
                disabled={isSaving}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-xl shadow-purple-600/20 flex items-center justify-center space-x-2 group disabled:opacity-50 cursor-pointer"
              >
                <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'} group-hover:scale-125 transition-transform text-[10px]`}></i>
                <span>{isSaving ? 'Salvando...' : 'Salvar Alterações das Cartas'}</span>
              </button>
            ) : (
              <Link
                to="/meus-decks?novo=1"
                className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-xl shadow-purple-600/20 flex items-center justify-center space-x-2 group"
              >
                <i className="fas fa-plus text-[10px]"></i>
                <span>Criar Baralho em Meus Decks</span>
              </Link>
            )}

            {currentDeckId && (
              <button
                onClick={handleDeleteDeck}
                className="w-full bg-slate-950/50 hover:bg-red-500/10 border border-slate-800 hover:border-red-500/20 text-slate-500 hover:text-red-400 p-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center space-x-2"
              >
                <i className="fas fa-trash-alt text-[10px]"></i>
                <span>Excluir Baralho</span>
              </button>
            )}
          </div>

          {/* Deck List / Grid View */}
          <div className="bg-slate-900/90 rounded-[2rem] border border-slate-800 overflow-hidden flex flex-col shadow-2xl max-h-[550px]">
            <div className="p-3.5 bg-slate-950/40 border-b border-slate-800 flex justify-between items-center">
              <div>
                <h4 className="text-[9px] font-black text-white uppercase tracking-[0.2em]">Composição</h4>
                <p className="text-[7px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                  {settings.deckDisplayMode === 'grid' ? 'Grid visual' : 'Lista detalhada'}
                </p>
              </div>

              <div className="flex items-center space-x-2.5">
                {/* Quick Toggle Lista / Grid */}
                <div className="flex bg-slate-900/80 p-0.5 rounded-lg border border-slate-800">
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...settings, deckDisplayMode: 'list' as const };
                      setSettings(updated);
                      saveDeckBuilderSettings(updated);
                    }}
                    className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider flex items-center space-x-1 transition-all ${settings.deckDisplayMode === 'list'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                      }`}
                    title="Modo Lista"
                  >
                    <i className="fas fa-list text-[8px]"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...settings, deckDisplayMode: 'grid' as const };
                      setSettings(updated);
                      saveDeckBuilderSettings(updated);
                    }}
                    className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider flex items-center space-x-1 transition-all ${settings.deckDisplayMode === 'grid'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                      }`}
                    title="Modo Grid"
                  >
                    <i className="fas fa-th-large text-[8px]"></i>
                  </button>
                </div>

                <div className="flex flex-col items-end border-l border-white/5 pl-2.5">
                  <span className={`text-xs font-black transition-colors ${totalCards > 60 ? 'text-red-500' : 'text-purple-400'}`}>
                    {totalCards}
                  </span>
                  <span className="text-[7px] text-slate-600 font-black uppercase tracking-widest">Total</span>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4 scrollbar-thin scrollbar-thumb-slate-800">
              {sectionedDeckItems.map(sec => (
                <div key={sec.section} className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center space-x-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${sec.isInvalid
                          ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]'
                          : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'
                        }`}></div>
                      <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{sec.section}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-[9px] text-slate-400 font-bold">{sec.totalCount}</span>
                      {sec.maxCount !== undefined && (
                        <span className="text-[8px] text-slate-600 font-medium">
                          / {sec.maxCount}
                        </span>
                      )}
                    </div>
                  </div>

                  {settings.deckDisplayMode === 'grid' ? (
                    /* MODO GRID VISUAL */
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {sec.items.map((item, idx) => (
                        <DeckGridItem
                          key={`${item.section}-${item.card.id || item.card.code || 'c'}-${idx}`}
                          item={item}
                          settings={settings}
                          onAdd={addToDeck}
                          onRemove={removeFromDeck}
                          onPreview={handlePreviewCard}
                        />
                      ))}
                    </div>
                  ) : (
                    /* MODO LISTA COMPACTA */
                    <div className="space-y-1">
                      {sec.items.map((item, idx) => (
                        <DeckListItem
                          key={`${item.section}-${item.card.id || item.card.code || 'c'}-${idx}`}
                          item={item}
                          settings={settings}
                          onAdd={addToDeck}
                          onRemove={removeFromDeck}
                          onPreview={handlePreviewCard}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {deckItems.length === 0 && (
                <div className="py-12 text-center space-y-3">
                  <div className="inline-flex w-12 h-12 rounded-full bg-slate-950/60 border border-dashed border-slate-800 items-center justify-center text-slate-800">
                    <i className="fas fa-ghost text-xl"></i>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-slate-600 font-black uppercase tracking-widest italic">Baralho vazio</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* COLUNA DIREITA (SEARCH & POOL) */}
        <div className="flex-1 space-y-4 min-w-0 flex flex-col">

          {/* Header & Search */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-[2rem] p-4 shadow-2xl">
            <div className="flex flex-col md:flex-row gap-3">
              <form onSubmit={handleSearch} className="flex-1 relative group">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors text-xs"></i>
                <input
                  type="text"
                  placeholder={`Buscar cartas em ${currentGameName || 'TCG'}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-950/40 border border-slate-800 text-xs px-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600/50 transition-all font-medium placeholder-slate-600 text-white"
                />
              </form>
              <div className="flex gap-2">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="flex-1 md:flex-none bg-purple-600 hover:bg-purple-500 text-white font-black uppercase tracking-[0.2em] text-[9px] px-6 rounded-xl shadow-xl shadow-purple-600/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex-1 md:flex-none border font-black uppercase tracking-[0.2em] text-[9px] px-6 py-3 rounded-xl transition-all active:scale-95 ${showFilters ? 'bg-purple-600/20 border-purple-500 text-white' : 'bg-slate-950/40 border-slate-800 text-slate-500 hover:text-white'}`}
                >
                  Filtros
                </button>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-4 mt-4 pt-4 border-t border-white/5">
                    {/* Linha de topo: Ordenação & Limpar Filtros */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-950/60 p-3 rounded-2xl border border-slate-800">
                      <div className="flex items-center space-x-2.5">
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider flex items-center space-x-1.5">
                          <i className="fas fa-sort text-purple-400"></i>
                          <span>Ordenação:</span>
                        </span>
                        <div className="relative">
                          <select
                            value={searchSort}
                            onChange={(e) => {
                              setSearchSort(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="bg-slate-950 border border-slate-700 hover:border-slate-600 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold appearance-none outline-none focus:border-purple-500 text-slate-100 cursor-pointer shadow-sm transition-all"
                          >
                            <option value="default" className="bg-slate-900 text-slate-100 py-1">Padrão / Relevância</option>
                            <option value="id-asc" className="bg-slate-900 text-slate-100 py-1">ID (Crescente)</option>
                            <option value="id-desc" className="bg-slate-900 text-slate-100 py-1">ID (Decrescente)</option>
                            <option value="name-asc" className="bg-slate-900 text-slate-100 py-1">Nome (A - Z)</option>
                            <option value="name-desc" className="bg-slate-900 text-slate-100 py-1">Nome (Z - A)</option>
                            <option value="code-asc" className="bg-slate-900 text-slate-100 py-1">Código (Crescente)</option>
                            <option value="code-desc" className="bg-slate-900 text-slate-100 py-1">Código (Decrescente)</option>
                            <option value="price-asc" className="bg-slate-900 text-slate-100 py-1">Preço (Menor p/ Maior)</option>
                            <option value="price-desc" className="bg-slate-900 text-slate-100 py-1">Preço (Maior p/ Menor)</option>
                          </select>
                          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                            <i className="fas fa-chevron-down text-[10px]"></i>
                          </div>
                        </div>
                      </div>

                      {(Object.keys(activeFilters).length > 0 || searchSort !== 'default') && (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveFilters({});
                            setSearchSort('default');
                            setCurrentPage(1);
                          }}
                          className="px-3 py-1.5 bg-pink-600/10 hover:bg-pink-600/20 border border-pink-500/20 text-pink-400 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
                        >
                          Limpar Filtros
                        </button>
                      )}
                    </div>

                    {/* Filtros Rápidos dinâmicos por TCG */}
                    {(() => {
                      const activeGameSlugLow = (currentGame?.slug || gameSlug || '').toLowerCase();
                      const quickOpts = GAME_FILTERS_CONFIG[activeGameSlugLow]?.quickOptions;

                      if (!quickOpts) {
                        return (
                          <p className="text-[9px] text-slate-600 font-bold uppercase tracking-wider py-1 text-center">
                            Nenhum filtro rápido configurado para este jogo.
                          </p>
                        );
                      }

                      return (
                        <div className="space-y-3.5 pt-1">
                          {Object.entries(quickOpts).map(([field, options]: [string, any]) => {
                            let displayOptions: string[] = options;
                            if ((field === 'type' || field === 'cardType') && slotAcceptedTypes && slotAcceptedTypes.length > 0) {
                              const acceptedLowers = slotAcceptedTypes.map((t: string) => t.toLowerCase());
                              const filtered = options.filter((opt: string) => acceptedLowers.includes(opt.toLowerCase()));
                              displayOptions = filtered.length > 0 ? filtered : slotAcceptedTypes;
                            }

                            return (
                              <div key={field} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">{pretty(field)}</label>
                                  {(field === 'type' || field === 'cardType') && slotAcceptedTypes && slotAcceptedTypes.length > 0 && (
                                    <span className="text-[8px] font-bold text-purple-400 uppercase tracking-wider">
                                      Tipos de {activeTab}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {displayOptions.map((opt: string) => {
                                    const isSelected = activeFilters[field] === opt;
                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        onClick={() => {
                                          setActiveFilters(prev => {
                                            const updated = { ...prev };
                                            if (updated[field] === opt) {
                                              delete updated[field];
                                            } else {
                                              updated[field] = opt;
                                            }
                                            return updated;
                                          });
                                          setCurrentPage(1);
                                        }}
                                        className={`px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-wider border transition-all ${isSelected
                                            ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-600/20'
                                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white hover:border-purple-500/50'
                                          }`}
                                      >
                                        {opt}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Acervo de Cartas com Seletor de Seções Integrado */}
          <div className="flex-1 bg-slate-900/90 border border-slate-800 rounded-[2.5rem] p-4 lg:p-6 shadow-2xl flex flex-col min-h-0 relative group">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-slate-800/80">
              <div>
                <h2 className="text-lg font-black text-white italic uppercase tracking-tighter">Acervo de Cartas</h2>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest flex items-center space-x-1.5 mt-0.5">
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-purple-400 text-[9px]"></i>
                      <span className="text-purple-400">Buscando cartas...</span>
                    </>
                  ) : (
                    <span>
                      {totalResults !== null && totalResults !== undefined
                        ? `${totalResults.toLocaleString()} ${totalResults === 1 ? 'resultado encontrado' : 'resultados encontrados'}`
                        : `${cards.length} ${cards.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}`}
                    </span>
                  )}
                </p>
              </div>

              {/* Seletor de Seções Integrado */}
              <div className="flex items-center space-x-1 overflow-x-auto scrollbar-hide bg-slate-950/60 p-1.5 rounded-2xl border border-slate-800/80 max-w-full">
                {sections.map(section => (
                  <button
                    key={section}
                    type="button"
                    onClick={() => {
                      setActiveTab(section);
                      setCurrentPage(1);
                    }}
                    className={`flex-shrink-0 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === section
                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                      }`}
                  >
                    <span>{section}</span>
                  </button>
                ))}
              </div>
            </div>

            <div ref={catalogContainerRef} className="flex-1 overflow-y-auto scrollbar-hide pr-1">
              {currentGame && currentGame.sauce !== 'homura' && currentGame.sauce !== 'kyubey' ? (
                <div className="h-64 flex flex-col items-center justify-center text-center p-6 space-y-4">
                  <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/20 rounded-full flex items-center justify-center text-amber-500 shadow-xl">
                    <i className="fas fa-wrench text-xl"></i>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-black text-white uppercase tracking-tight">Em Construção</p>
                    <p className="text-[10px] text-slate-500 font-bold leading-relaxed max-w-[200px] mx-auto uppercase">
                      Ainda não é possível buscar cartas de {currentGame.name}. Estamos trabalhando nisso!
                    </p>
                  </div>
                </div>
              ) : loading ? (
                <div className="h-64 flex flex-col items-center justify-center space-y-3">
                  <div className="w-10 h-10 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
                  <p className="text-[8px] font-black uppercase tracking-widest text-slate-600 animate-pulse">Consultando oráculo...</p>
                </div>
              ) : (
                <div className={
                  settings.gridSize === 'compact'
                    ? "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-2.5"
                    : settings.gridSize === 'large'
                      ? "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5"
                      : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 2xl:grid-cols-6 gap-4"
                }>
                  {cards.map((card, idx) => (
                    <CatalogCardItem
                      key={`${card.id || card.code || 'c'}-${idx}`}
                      card={card}
                      idx={idx}
                      inDeckCount={cardCountsMap.get(card.id, card) || 0}
                      settings={settings}
                      onAdd={addToDeck}
                      onPreview={handlePreviewCard}
                    />
                  ))}
                  {cards.length === 0 && (
                    <div className="col-span-full py-16 flex flex-col items-center justify-center text-center space-y-3">
                      <div className="w-12 h-12 rounded-2xl bg-slate-950/60 border border-slate-800 flex items-center justify-center text-slate-600">
                        <i className="fas fa-search text-base text-slate-500"></i>
                      </div>
                      <div className="space-y-1">
                        <p className="text-white font-bold text-sm">Carta não encontrada</p>
                        <p className="text-slate-500 text-xs max-w-xs">
                          {searchQuery
                            ? `Nenhuma carta encontrada para "${searchQuery}".`
                            : 'Nenhuma carta disponível com os filtros atuais.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (() => {
              const maxButtons = 5;
              let startPage = Math.max(1, currentPage - 2);
              let endPage = Math.min(totalPages, startPage + maxButtons - 1);
              if (endPage - startPage + 1 < maxButtons) {
                startPage = Math.max(1, endPage - maxButtons + 1);
              }
              const pageNumbers: number[] = [];
              for (let p = startPage; p <= endPage; p++) {
                pageNumbers.push(p);
              }

              return (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 mt-6 border-t border-white/5">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    Página <span className="text-purple-400 font-extrabold">{currentPage}</span> de <span className="text-white">{totalPages}</span>
                    {totalResults !== null && totalResults !== undefined && (
                      <span className="text-slate-500 ml-2">({totalResults.toLocaleString()} cartas)</span>
                    )}
                  </div>

                  <div className="flex items-center space-x-1.5">
                    {/* Primeira Página */}
                    <button
                      type="button"
                      title="Primeira página"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(1)}
                      className="w-9 h-9 flex items-center justify-center bg-slate-950/60 border border-slate-800 rounded-xl text-slate-500 hover:text-white hover:border-purple-500 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-angles-left text-[10px]"></i>
                    </button>

                    {/* Anterior */}
                    <button
                      type="button"
                      title="Página anterior"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="w-9 h-9 flex items-center justify-center bg-slate-950/60 border border-slate-800 rounded-xl text-slate-500 hover:text-white hover:border-purple-500 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-chevron-left text-[10px]"></i>
                    </button>

                    {/* Ellipsis inicial se startPage > 1 */}
                    {startPage > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => handlePageChange(1)}
                          className="w-9 h-9 rounded-xl text-[10px] font-black bg-slate-950/60 text-slate-500 hover:text-white border border-slate-800 transition-all"
                        >
                          1
                        </button>
                        {startPage > 2 && (
                          <span className="px-1 text-slate-600 text-xs font-bold">...</span>
                        )}
                      </>
                    )}

                    {/* Números de página da janela */}
                    {pageNumbers.map(pageNum => (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-9 h-9 rounded-xl text-[10px] font-black transition-all ${currentPage === pageNum
                            ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30 font-extrabold border border-purple-500'
                            : 'bg-slate-950/60 text-slate-400 hover:text-white hover:border-slate-700 border border-slate-800'
                          }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                    {/* Ellipsis final se endPage < totalPages */}
                    {endPage < totalPages && (
                      <>
                        {endPage < totalPages - 1 && (
                          <span className="px-1 text-slate-600 text-xs font-bold">...</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handlePageChange(totalPages)}
                          className="w-9 h-9 rounded-xl text-[10px] font-black bg-slate-950/60 text-slate-500 hover:text-white border border-slate-800 transition-all"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}

                    {/* Próxima */}
                    <button
                      type="button"
                      title="Próxima página"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="w-9 h-9 flex items-center justify-center bg-slate-950/60 border border-slate-800 rounded-xl text-slate-500 hover:text-white hover:border-purple-500 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-chevron-right text-[10px]"></i>
                    </button>

                    {/* Última Página */}
                    <button
                      type="button"
                      title="Última página"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                      className="w-9 h-9 flex items-center justify-center bg-slate-950/60 border border-slate-800 rounded-xl text-slate-500 hover:text-white hover:border-purple-500 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-angles-right text-[10px]"></i>
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Modal de Exportação */}
      <AnimatePresence>
        {showExportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowExportModal(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-xl w-full bg-slate-900 rounded-3xl border border-slate-800 shadow-[0_0_50px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-slate-950/40">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                    <i className="fas fa-file-export text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white uppercase tracking-wider">Exportar Baralho</h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      {gameSlug === 'one-piece' ? 'One Piece TCG • Formato Oficial' : `${currentGame?.name || 'TCG'} • Lista de Baralho`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="w-8 h-8 hover:bg-slate-800 text-slate-400 hover:text-white rounded-xl flex items-center justify-center transition-all"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                <div className="relative">
                  <textarea
                    readOnly
                    value={exportText}
                    rows={12}
                    className="w-full bg-slate-950 text-slate-200 font-mono text-xs p-4 rounded-2xl border border-slate-800 focus:outline-none focus:border-purple-500/50 resize-none scrollbar-thin scrollbar-thumb-slate-800 select-all leading-relaxed"
                  />
                  <div className="absolute top-3 right-3 text-[9px] font-black uppercase tracking-widest text-purple-400 bg-slate-900/90 px-2.5 py-1 rounded-lg border border-purple-500/20">
                    {deckItems.reduce((a, b) => a + b.quantity, 0)} Cartas
                  </div>
                </div>

                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-relaxed">
                  {gameSlug === 'one-piece'
                    ? 'Copie a lista acima no padrão oficial do One Piece TCG para o OPTCG Sim, planilhas ou torneios.'
                    : 'Copie a lista acima para compartilhar ou importar em outros leitores de decks.'}
                </p>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-white/5 bg-slate-950/60 flex items-center justify-end space-x-3">
                <button
                  onClick={handleDownloadTxt}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all flex items-center space-x-2"
                >
                  <i className="fas fa-download text-xs text-slate-400"></i>
                  <span>Baixar .TXT</span>
                </button>
                <button
                  onClick={handleCopyExportText}
                  className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center space-x-2 shadow-xl ${copiedExport
                      ? 'bg-emerald-600 text-white shadow-emerald-600/30'
                      : 'bg-purple-600 hover:bg-purple-500 text-white shadow-purple-600/30 active:scale-95'
                    }`}
                >
                  <i className={`fas ${copiedExport ? 'fa-check' : 'fa-copy'} text-xs`}></i>
                  <span>{copiedExport ? 'Copiado!' : 'Copiar Lista'}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded Card Modal */}
      <CardDetailModal
        card={expandedCard}
        onClose={() => setExpandedCard(null)}
        cardsList={cards}
        onSelectCard={(card) => setExpandedCard(card)}
        gameName={typeof activeGame === 'string' ? activeGame : undefined}
        primaryAction={{
          label: 'Adicionar ao Baralho',
          icon: 'fas fa-plus',
          onClick: (card) => {
            addToDeck(card);
            setExpandedCard(null);
          }
        }}
      />

      {/* Import Deck Modal */}
      <ImportDeckModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        gameSlug={gameSlug}
        activeGame={activeGame}
        onImportComplete={handleImportComplete}
      />

      {/* Settings Modal */}
      <DeckBuilderSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={settings}
        onUpdateSettings={setSettings}
        activeGameName={currentGame?.name || String(activeGame)}
      />

      {/* Modal de Confirmação: Limpar Deck */}
      <AnimatePresence>
        {confirmClearOpen && (
          <div
            id="clear-deck-modal-backdrop"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85"
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmClearOpen(false);
            }}
          >
            <motion.div
              id="clear-deck-modal-dialog"
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-md bg-slate-900 border border-red-500/30 rounded-3xl p-6 shadow-2xl space-y-5"
            >
              <div className="flex items-center space-x-3.5">
                <div className="w-12 h-12 rounded-2xl bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 shrink-0">
                  <i className="fas fa-trash-alt text-xl"></i>
                </div>
                <div>
                  <h3 className="text-base font-black text-white uppercase italic">
                    Limpar Baralho?
                  </h3>
                  <p className="text-xs text-slate-400 font-medium">
                    Todas as {deckItems.reduce((acc, curr) => acc + curr.quantity, 0)} cartas adicionadas serão removidas.
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-300 bg-slate-950/60 p-3.5 rounded-2xl border border-white/5 leading-relaxed">
                Esta ação removerá todas as cartas do baralho atual em edição. Essa alteração não poderá ser desfeita.
              </p>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  id="cancel-clear-deck-button"
                  onClick={() => setConfirmClearOpen(false)}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-black uppercase tracking-wider rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  id="confirm-clear-deck-button"
                  onClick={handleConfirmClearDeck}
                  className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-red-600/30 transition-all flex items-center space-x-2 active:scale-95"
                >
                  <i className="fas fa-trash-alt text-[10px]"></i>
                  <span>Sim, Limpar</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Confirmação: Excluir Baralho Salvo */}
      <AnimatePresence>
        {confirmDeleteOpen && (
          <div
            id="delete-deck-modal-backdrop"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85"
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmDeleteOpen(false);
            }}
          >
            <motion.div
              id="delete-deck-modal-dialog"
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-md bg-slate-900 border border-red-500/30 rounded-3xl p-6 shadow-2xl space-y-5"
            >
              <div className="flex items-center space-x-3.5">
                <div className="w-12 h-12 rounded-2xl bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 shrink-0">
                  <i className="fas fa-exclamation-triangle text-xl"></i>
                </div>
                <div>
                  <h3 className="text-base font-black text-white uppercase italic">
                    Excluir Baralho Salvo?
                  </h3>
                  <p className="text-xs text-slate-400 font-medium">
                    O baralho <strong className="text-white">"{deckName || 'Sem nome'}"</strong> será apagado permanentemente.
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-300 bg-slate-950/60 p-3.5 rounded-2xl border border-white/5 leading-relaxed">
                Você perderá o acesso a esta lista e o link público associado deixará de funcionar.
              </p>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  id="cancel-delete-deck-button"
                  onClick={() => setConfirmDeleteOpen(false)}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-black uppercase tracking-wider rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  id="confirm-delete-deck-button"
                  onClick={handleConfirmDeleteDeck}
                  className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-red-600/30 transition-all flex items-center space-x-2 active:scale-95"
                >
                  <i className="fas fa-trash-alt text-[10px]"></i>
                  <span>Sim, Excluir</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
